import { insertImportsAtModuleScope } from "@/lib/validation/directive-predicates";

/**
 * Canonical PRODUCTION Panda origin.
 *
 * The badge is baked as source into a user's generated app and then served
 * from THAT app's own domain (a custom domain, a *.vercel.app, or a Panda
 * subdomain). So every URL inside the badge — the icon asset and the click
 * link — must be an absolute production URL. It can NEVER depend on whatever
 * `NEXT_PUBLIC_SITE_URL` the *generating* server happened to run with: in local
 * dev / preview builds that value is `http://localhost:3000`, and baking it in
 * shipped a broken icon and a dead localhost link to every published example
 * (PANDA-1460). This constant is the single source of truth for that.
 */
export const PANDA_PRODUCTION_URL = "https://panda.new";

const DEFAULT_SITE_URL = PANDA_PRODUCTION_URL;

/**
 * Panda's OWN site origin, env-overridable for staging/local runs.
 *
 * Use this only for surfaces rendered ON a Panda-controlled domain. Do NOT use
 * it for anything baked into a generated app — that must use
 * `PANDA_PRODUCTION_URL` so a stale dev/preview value cannot leak out to a
 * published site (PANDA-1460).
 */
export function getSiteUrl(): string {
  return process.env.NEXT_PUBLIC_SITE_URL ?? DEFAULT_SITE_URL;
}

/**
 * URL the badge links to — routes through our tracking endpoint on production
 * Panda, then redirects to the homepage. Always absolute + production so the
 * link resolves from any published app domain, subdomain, desktop or mobile,
 * and never against `localhost` or the generated app's own origin.
 */
export function getBadgeClickUrl(projectId: string): string {
  return `${PANDA_PRODUCTION_URL}/api/badge-click?project_id=${encodeURIComponent(projectId)}&ref=${encodeURIComponent(projectId)}`;
}

/**
 * Where the INJECTED badge sits inside a published app, in px.
 *
 * The workspace preview shows a published-and-unchanged project by pointing its
 * iframe at the deployed build, which carries this baked-in badge instead of the
 * React overlay (components/built-with-badge.tsx). That badge lives in another
 * origin, so its "remove branding" X has to be drawn by the PARENT, positioned
 * over the badge's top-right corner — which means the parent needs these exact
 * numbers.
 *
 * Anchoring is bottom-right and the X hangs off the badge's right edge, so the
 * only inputs are `bottom`, `right` and `height`. The badge's WIDTH is set by
 * its text and never enters the maths.
 *
 * These are the single source of truth: the CSS below is asserted against them
 * in tests/unit/lib/badge.test.ts, so the generated badge and the overlay X can
 * never drift apart silently.
 */
export const INJECTED_BADGE_GEOMETRY = {
  /** Default (>640px viewport). */
  desktop: { bottom: 20, right: 20, height: 40 },
  /** Phones (≤640px), matching the badge CSS media query below. */
  phone: { bottom: 12, right: 12, height: 32 },
} as const;

/** The viewport width at/below which the injected badge uses its phone
 *  geometry. Evaluated against the IFRAME's width, not the browser window —
 *  a 375px device-view preview inside a wide desktop window is a phone here. */
export const INJECTED_BADGE_PHONE_MAX_WIDTH = 640;

/**
 * Where the OVERLAY badge sits inside the preview box, in px — the React twin
 * of {@link INJECTED_BADGE_GEOMETRY} above.
 *
 * Same pill, same corner, drawn by the parent instead of baked into the app
 * (components/built-with-badge.tsx renders it whenever the preview is showing
 * live sandbox files rather than a deployed build). It lives here, beside the
 * injected numbers, for the reason the injected ones do: anything else anchored
 * to the preview's bottom edge has to know how tall a column the badge occupies
 * in order to stay off it, and whichever of the two variants is on screen, the
 * answer comes from one place.
 *
 * `height` is the pill's own box; the remove-× hangs a few px above it and is
 * positioned from the component's REMOVE_OFFSET, not from here. Asserted
 * against the rendered element in tests/unit/components/built-with-badge.test.tsx.
 */
export const OVERLAY_BADGE_GEOMETRY = {
  /** Default (window >640px). */
  desktop: { bottom: 16, right: 16, height: 40 },
  /** Phones — the window at/below INJECTED_BADGE_PHONE_MAX_WIDTH, resolved by
   *  hooks/use-badge-phone-viewport.ts. */
  phone: { bottom: 12, right: 12, height: 32 },
} as const;

/** Loopback / dev hosts that only resolve on the machine that generated the
 *  app — never on the published site. */
const NON_PRODUCTION_BADGE_URL =
  /https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(?::\d+)?/i;

/**
 * Publish-time guardrail. Asserts that generated badge content contains only
 * absolute production URLs and throws on any localhost/loopback origin.
 * Exported so template publishing + CI can enforce the same invariant — the
 * "automated checks reject template links containing localhost" acceptance
 * criterion of PANDA-1460.
 */
export function assertProductionBadgeUrls(content: string): void {
  const match = content.match(NON_PRODUCTION_BADGE_URL);
  if (match) {
    throw new Error(
      `Panda badge would ship a non-production URL (${match[0]}). Badge asset ` +
        `and link URLs must be absolute ${PANDA_PRODUCTION_URL} URLs.`,
    );
  }
}

/**
 * Generates the content of `components/_panda_badge.tsx` to inject into a
 * generated project.
 *
 * HARDENING STRATEGY — why this approach is tamper-proof:
 *
 * 1. The badge is rendered as a real DOM element inserted at the end of
 *    <body> by the server-rendered layout (not by client-side JS). This means
 *    it appears in the initial HTML sent to the browser, not after hydration.
 *
 * 2. The badge element's styles are locked via a <style> block injected into
 *    <head> using `!important` on every visual property. Even if a user adds
 *    a CSS rule targeting the element, the !important declarations win.
 *
 * 3. The <style> block targets the element by a unique data attribute
 *    (`data-panda-badge`) rather than a class name, so renaming the class in
 *    DevTools does not remove the styles.
 *
 * 4. `display:block !important` + `visibility:visible !important` +
 *    `opacity:1 !important` prevent the three most common CSS hide tricks.
 *
 * 5. The component file has `"use server"` — it runs on the server only and
 *    is not included in the client bundle, so it cannot be patched at runtime.
 *
 * 6. Removal requires editing the GitHub source and re-deploying — only
 *    possible if the user has push access. Paid users get a toggle in project
 *    settings that re-pushes without the badge instead.
 */
export function generateBadgeComponentContent(projectId: string): string {
  const href = getBadgeClickUrl(projectId);
  // Assets are baked into the published app and served from its own domain, so
  // they must resolve to production Panda — never the generating server's env
  // (see PANDA_PRODUCTION_URL). getSiteUrl() here shipped a localhost icon that
  // 404'd on the live site (PANDA-1460).
  const siteUrl = PANDA_PRODUCTION_URL;

  // Unique attribute used to lock styles — harder to remove than a class name.
  const ATTR = "data-panda-badge";

  const css = `
[${ATTR}] {
  position: fixed !important;
  bottom: 20px !important;
  right: 20px !important;
  z-index: 2147483647 !important;
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
  pointer-events: auto !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  gap: 6px !important;
  padding: 0 14px !important;
  background: #0A0A0A !important;
  border-radius: 8px !important;
  text-decoration: none !important;
  box-shadow: 0 3px 15px 0 rgba(0, 0, 0, 0.28) !important;
  cursor: pointer !important;
  max-width: none !important;
  min-width: 0 !important;
  width: auto !important;
  height: 40px !important;
  transform: none !important;
  clip: auto !important;
  clip-path: none !important;
  overflow: visible !important;
  transition: opacity 0.15s ease !important;
  line-height: 1 !important;
  white-space: nowrap !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Inter", sans-serif !important;
}
[${ATTR}]:hover { opacity: 0.85 !important; }
[${ATTR}] .__pb-icon {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
  width: 22px !important;
  height: 22px !important;
  border-radius: 50% !important;
  overflow: hidden !important;
  background: transparent !important;
}
[${ATTR}] .__pb-icon img {
  width: 22px !important;
  height: 22px !important;
  object-fit: cover !important;
  object-position: left !important;
  display: block !important;
  border-radius: 50% !important;
}
/* Single-line label. Every part is inline and nowrap so the badge can never
   wrap, stack or split — the "Remix with [logo] Panda" lockup stays on one
   line at every viewport width. */
[${ATTR}] .__pb-text,
[${ATTR}] .__pb-brand {
  display: inline-block !important;
  font-size: 13px !important;
  line-height: 20px !important;
  white-space: nowrap !important;
  flex-shrink: 0 !important;
}
[${ATTR}] .__pb-text {
  font-weight: 400 !important;
  color: #9CA3AF !important;
}
[${ATTR}] .__pb-brand {
  font-weight: 500 !important;
  color: #D4D4D8 !important;
}
/* Mobile (phones ≤640px): same one-line lockup, just tightened. The badge
   shrinks its own footprint (smaller padding/height/type) instead of
   dropping the label, so it stays out of the way of hero CTAs and the
   mobile chat controls (PANDA-1251: "no important content hidden behind
   Panda UI overlays") while keeping the copy intact on a single line.
   Locked with !important like the rest, scoped to the same data attribute. */
@media (max-width: 640px) {
  [${ATTR}] {
    bottom: 12px !important;
    right: 12px !important;
    gap: 5px !important;
    padding: 0 10px !important;
    height: 32px !important;
  }
  [${ATTR}] .__pb-icon,
  [${ATTR}] .__pb-icon img {
    width: 18px !important;
    height: 18px !important;
  }
  [${ATTR}] .__pb-text,
  [${ATTR}] .__pb-brand {
    font-size: 12px !important;
    line-height: 18px !important;
  }
}`.trim();

  const content = `// Built with Panda — ${siteUrl}
// This file is managed by Panda. Do not edit or remove it.
// Removing or hiding this badge requires a paid Panda account.
// DO NOT add "use client" — this is a React Server Component. It renders
// in the initial HTML response and cannot be patched by client-side JS.

/**
 * React Server Component — no "use client" directive means Next.js renders
 * this entirely on the server. The badge appears in the first HTML byte
 * sent to the browser, not after JS hydration.
 */
export function PandaBadge() {
  return (
    <>
      {/* Styles injected server-side with !important on every prop.
          Targets data attribute — class renames in DevTools have no effect. */}
      <style
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: ${JSON.stringify(css)} }}
      />
      <a
        href="${href}"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Remix with Panda"
        data-panda-badge="1"
      >
        {/* Single line: "Remix with" + Panda logo + "Panda" */}
        <span className="__pb-text">Remix with</span>
        <span className="__pb-icon">
          <img src="${siteUrl}/panda-lockup.png" alt="" aria-hidden="true" />
        </span>
        <span className="__pb-brand">Panda</span>
      </a>
    </>
  );
}
`;

  // Publish-time guardrail: a generated badge must carry ONLY absolute
  // production URLs. If a future edit ever reintroduces an env-derived or
  // relative URL, fail loudly here instead of silently shipping a broken icon
  // and a dead localhost link to a published site (PANDA-1460).
  assertProductionBadgeUrls(content);

  return content;
}

/**
 * Injects the PandaBadge import + JSX into a layout.tsx file's content.
 * Targets </body> first, falls back to {children}.
 */
export function injectBadgeIntoLayout(content: string): string {
  // Idempotent: skip if already injected
  if (content.includes("_panda_badge")) return content;

  const importLine = `import { PandaBadge } from "@/components/_panda_badge";`;

  // Shared, directive-aware inserter. The hand-rolled version here ended in
  // `lines.unshift(importLine)` when the layout had no imports to follow, which
  // on a `"use client"` layout put the import ABOVE the directive and produced
  // the exact `The "use client" directive must be placed before other
  // expressions` build failure. injectPandaBadge runs AFTER the publish
  // syntax/framework gate, so nothing downstream would have caught it.
  content = insertImportsAtModuleScope(content, [importLine]);

  // Inject <PandaBadge /> just before </body>
  if (/^([ \t]*)<\/body>/m.test(content)) {
    // </body> is on its own line — insert badge above it
    content = content.replace(
      /^([ \t]*)<\/body>/m,
      `$1  <PandaBadge />\n$1</body>`
    );
  } else if (content.includes("</body>")) {
    // </body> is inline (e.g. <body>{children}</body>) — insert badge before it
    content = content.replace("</body>", "<PandaBadge /></body>");
  } else if (content.includes("{children}")) {
    content = content.replace("{children}", "{children}\n        <PandaBadge />");
  }

  return content;
}

/**
 * Removes the PandaBadge from a layout.tsx content string.
 * Used when a paid user disables the badge for their project.
 */
export function removeBadgeFromLayout(content: string): string {
  // Remove import line
  content = content.replace(/^import\s+\{[^}]*PandaBadge[^}]*\}\s+from\s+["']@\/components\/_panda_badge["'];?\n?/m, "");
  // Remove JSX usage (with or without surrounding whitespace/newlines)
  content = content.replace(/[ \t]*<PandaBadge\s*\/>\n?/g, "");
  return content;
}
